import React, { useState } from "react";
import ImpactCard from "@/components/ImpactCard";
import SharePlatforms from "@/components/SharePlatforms";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

// Builds the exact card payload from the user's current privacy selections — used for preview, PNG and native share.
export function buildCardData(impact, { displayName, showFinancial, showTime, showScore, milestone }) {
  const hasScore = impact.minutescore !== null && impact.minutescore !== undefined;
  return {
    display_name: displayName || "A MinuteSwap founder",
    milestone: milestone || undefined,
    moves_created: impact.moves_created ?? 0,
    blockers_cleared: impact.blockers_cleared ?? 0,
    actions_completed: impact.actions_completed ?? 0,
    action_rate: impact.action_rate ?? 0,
    ...(showTime ? { time_saved_label: impact.time_saved_label } : {}),
    ...(showFinancial ? { money_impact: impact.money_impact } : {}),
    ...(showScore && hasScore ? { minutescore: impact.minutescore, tier: impact.tier, score_status: impact.score_status } : {}),
  };
}

export default function ShareModal({ open, onOpenChange, impact, milestones }) {
  const [displayName, setDisplayName] = useState(impact.display_name || "");
  const [showFinancial, setShowFinancial] = useState(true);
  const [showTime, setShowTime] = useState(true);
  const [showScore, setShowScore] = useState(true);
  const [milestone, setMilestone] = useState("");
  const [publicUrl, setPublicUrl] = useState("");

  const cardData = buildCardData(impact, { displayName, showFinancial, showTime, showScore, milestone });

  // Public link is created lazily with the CURRENT privacy selections the first time a platform needs it.
  const ensurePublicUrl = async () => {
    try {
      const { data } = await api.post("/share", {
        display_name: displayName, show_financial: showFinancial,
        show_time_saved: showTime, show_minutescore: showScore, milestone: milestone || null,
      });
      const url = `${window.location.origin}/impact/${data.public_id}`;
      setPublicUrl(url);
      return url;
    } catch { toast.error("Could not create public link"); throw new Error("share failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden border-white/12 bg-[var(--surface)] text-white sm:max-w-lg" data-testid="share-modal">
        <DialogHeader><DialogTitle className="font-head text-2xl font-extrabold">Share your impact</DialogTitle></DialogHeader>

        <div className="rounded-xl"><ImpactCard data={cardData} /></div>

        <div className="mt-2 space-y-4">
          <SharePlatforms card={cardData} publicUrl={publicUrl} ensurePublicUrl={ensurePublicUrl} />
          <p className="-mt-2 text-center text-[11px] text-neutral-600">1080 × 1080 PNG · honours the privacy controls below · Instagram, TikTok &amp; WeChat use your device's share sheet where available</p>

          <div>
            <Label className="text-neutral-300">Display name</Label>
            <Input data-testid="share-display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-1.5 border-white/12 bg-[var(--surface-raised)] text-white" />
          </div>

          {milestones.length > 0 && (
            <div>
              <Label className="text-neutral-300">Feature a milestone <span className="text-neutral-600">(optional)</span></Label>
              <div className="mt-2 flex flex-wrap gap-2">
                <button data-testid="share-milestone-none" onClick={() => setMilestone("")} className={`rounded-full border px-3 py-1.5 text-xs ${!milestone ? "border-[var(--amber)] text-white" : "border-white/12 text-neutral-400"}`}>None</button>
                {milestones.map((m) => (
                  <button key={m.key} data-testid={`share-milestone-${m.key}`} onClick={() => setMilestone(m.unlocked_label)} className={`rounded-full border px-3 py-1.5 text-xs ${milestone === m.unlocked_label ? "border-[var(--amber)] bg-[var(--amber)]/10 text-white" : "border-white/12 text-neutral-400"}`}>{m.unlocked_label}</button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3 rounded-xl border border-white/10 bg-[var(--surface-raised)] p-4">
            <p className="font-mono2 text-[10px] uppercase tracking-wider text-neutral-500">Privacy controls</p>
            {[["Show reported financial impact", showFinancial, setShowFinancial, "toggle-financial"],
              ["Show time saved", showTime, setShowTime, "toggle-time"],
              ["Show MinuteScore", showScore, setShowScore, "toggle-score"]].map(([label, val, set, tid]) => (
              <div key={tid} className="flex items-center justify-between">
                <span className="text-sm text-neutral-300">{label}</span>
                <Switch data-testid={tid} checked={val} onCheckedChange={set} />
              </div>
            ))}
            <p className="text-xs text-neutral-600">Your original business questions are never shown on a shared card.</p>
          </div>

          <p className="text-center text-xs text-neutral-600">Clear your next blocker at MinuteSwap</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
